/** Metadaten-Werkstatt.
 *
 *  Die Lücken sichtbar machen, statt sie im Regal verschwinden zu lassen:
 *  fehlende Cover, fehlende ISBN (bei Mario 15 Bände), fehlende Inhaltsangabe,
 *  fehlende Schlagworte, fehlendes Lesejahr. Jede Zeile lässt sich direkt
 *  nachbessern — nachladen, nachschlagen oder von Hand eintragen.
 */

import { useCallback, useEffect, useState } from "react";
import type { Book } from "../../../shared/types";
import * as api from "../lib/api";
import { useStore } from "../lib/store";

type Fach = "ohneCover" | "ohneIsbn" | "ohneText" | "ohneTags" | "ohneJahr";

const FAECHER: { key: Fach; name: string; hinweis: string }[] = [
  {
    key: "ohneText",
    name: "ohne Inhaltsangabe",
    hinweis: "Nachladen holt sie von Open Library, sonst von der KI.",
  },
  {
    key: "ohneCover",
    name: "ohne Umschlag",
    hinweis: "Ohne ISBN gibt es kein Cover — erst die ISBN ergänzen.",
  },
  {
    key: "ohneIsbn",
    name: "ohne ISBN",
    hinweis: "Über Titel und Autor nachschlagen und übernehmen.",
  },
  {
    key: "ohneTags",
    name: "ohne Schlagworte",
    hinweis: "Die Verschlagwortung läuft stapelweise über die KI.",
  },
  {
    key: "ohneJahr",
    name: "ohne Lesejahr",
    hinweis: "Im Export nicht gepflegt — von Hand ergänzbar.",
  },
];

function Zeile({ book, onAendern }: { book: Book; onAendern: () => void }) {
  const { owner, schreibe, ersetze } = useStore();
  const [laeuft, setLaeuft] = useState(false);
  const [treffer, setTreffer] = useState<api.LookupResult | null>(null);

  async function nachschlagen() {
    setLaeuft(true);
    try {
      const res = await api.lookup(
        book.isbn
          ? { isbn: book.isbn }
          : { title: book.title, author: book.author },
      );
      setTreffer(res.data);
      if (!res.data) schreibe(async () => res);
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <article
      className="karte"
      style={{
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
        alignItems: "flex-start",
      }}
    >
      <div style={{ flex: "1 1 280px" }}>
        <strong style={{ fontFamily: "var(--book)", fontSize: 16 }}>
          {book.title}
        </strong>
        <p className="klein" style={{ marginTop: 4 }}>
          {book.author || "Autor unbekannt"}
          {book.isbn ? ` · ${book.isbn}` : " · keine ISBN"}
        </p>
        {treffer && (
          <div className="karte" style={{ marginTop: 10 }}>
            <p className="lauftext" style={{ fontSize: 14, margin: 0 }}>
              Gefunden: <strong>{treffer.title}</strong> von{" "}
              {treffer.author || "?"}
              {treffer.pub_year ? ` (${treffer.pub_year})` : ""}
              {treffer.isbn ? ` · ISBN ${treffer.isbn}` : ""}
              {treffer.pages ? ` · ${treffer.pages} Seiten` : ""}
            </p>
            {treffer.description && (
              <p className="lauftext" style={{ fontSize: 14 }}>
                {treffer.description}
              </p>
            )}
            <button
              className="knopf stark"
              style={{ marginTop: 8 }}
              onClick={async () => {
                const neu = await schreibe(
                  () =>
                    api.saveBook(owner, book.id, {
                      isbn: book.isbn || treffer.isbn,
                      pages: book.pages || treffer.pages,
                      pub_year: book.pub_year || treffer.pub_year,
                      publisher: book.publisher || treffer.publisher,
                      description: book.description || treffer.description,
                    }),
                  "Angaben übernommen.",
                );
                if (neu) {
                  ersetze(neu);
                  setTreffer(null);
                  onAendern();
                }
              }}
            >
              Übernehmen
            </button>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          className="knopf"
          disabled={laeuft}
          onClick={() => void nachschlagen()}
        >
          {laeuft ? "sucht …" : "Nachschlagen"}
        </button>
        <button
          className="knopf"
          onClick={async () => {
            const neu = await schreibe(() => api.enrichBook(owner, book.id));
            if (neu) {
              ersetze(neu);
              onAendern();
            }
          }}
        >
          Automatisch ergänzen
        </button>
      </div>
    </article>
  );
}

export function Werkstatt() {
  const { owner, modus, ki, schreibe, neuLaden, melde } = useStore();
  const [daten, setDaten] = useState<api.Workshop | null>(null);
  const [fach, setFach] = useState<Fach>("ohneText");
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const holen = useCallback(async () => {
    if (modus !== "server") {
      setFehler("Die Werkstatt braucht den Server.");
      return;
    }
    try {
      const res = await api.workshop(owner);
      setDaten(res.data);
      setFehler(null);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    }
  }, [owner, modus]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const liste = daten ? daten[fach] : [];

  return (
    <>
      <section style={{ maxWidth: "48ch" }}>
        <h2 className="titelzeile">Werkstatt</h2>
        <p className="lauftext">
          Hier stehen die Lücken. Ohne diese Ansicht bleiben sie für immer
          unsichtbar und das Regal hat Löcher, die niemand bemerkt.
        </p>
      </section>

      {fehler && (
        <div
          className="karte"
          style={{ borderLeft: "3px solid var(--warn)", marginTop: 20 }}
        >
          {fehler}
        </div>
      )}

      {daten && (
        <>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              margin: "26px 0 16px",
            }}
          >
            {FAECHER.map((f) => (
              <button
                key={f.key}
                className={`chip${fach === f.key ? " an" : ""}`}
                onClick={() => setFach(f.key)}
              >
                {f.name}{" "}
                <span style={{ opacity: 0.55 }}>{daten[f.key].length}</span>
              </button>
            ))}
          </div>

          <p className="lauftext" style={{ fontSize: 14 }}>
            {FAECHER.find((f) => f.key === fach)?.hinweis}
          </p>

          {fach === "ohneTags" && (
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
                margin: "14px 0",
              }}
            >
              <button
                className="knopf stark"
                disabled={!ki || laeuft || !daten.ohneTags.length}
                onClick={async () => {
                  setLaeuft(true);
                  const res = await schreibe(() => api.runTagging(owner, 20));
                  setLaeuft(false);
                  if (res) {
                    await holen();
                    await neuLaden();
                  }
                }}
              >
                {laeuft ? "verschlagwortet …" : "20 Bücher verschlagworten"}
              </button>
              {!ki && (
                <span className="klein" style={{ color: "var(--warn)" }}>
                  Kein KI-Schlüssel hinterlegt — die Stapelverarbeitung ist
                  abgeschaltet.
                </span>
              )}
            </div>
          )}

          {fach === "ohneJahr" && (
            <p className="lauftext" style={{ fontSize: 14 }}>
              Das Lesejahr lässt sich direkt im aufgeschlagenen Buch eintragen.
            </p>
          )}

          <div style={{ display: "grid", gap: 12, marginTop: 20 }}>
            {liste.slice(0, 40).map((b) => (
              <Zeile
                key={b.id}
                book={b}
                onAendern={() => {
                  void holen();
                  void neuLaden();
                }}
              />
            ))}
          </div>

          {liste.length > 40 && (
            <p className="klein" style={{ marginTop: 16 }}>
              … und {liste.length - 40} weitere. Erst diese abarbeiten, dann neu
              laden.
            </p>
          )}
          {!liste.length && (
            <p className="lauftext" style={{ marginTop: 20 }}>
              Nichts zu tun — in diesem Fach ist alles vollständig.
            </p>
          )}

          <hr className="trennlinie" />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              className="knopf"
              onClick={async () => {
                const res = await schreibe(() => api.scanSeriesNow(owner));
                if (res) {
                  await neuLaden();
                  melde("Reihen neu erkannt.", "ok");
                }
              }}
            >
              Reihen neu erkennen
            </button>
            <button className="knopf" onClick={() => void holen()}>
              Werkstatt neu laden
            </button>
          </div>
        </>
      )}
    </>
  );
}
